import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  Pressable,
  Animated,
  Platform,
  Dimensions,
  Easing,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RootStackParamList } from '../navigation/stackTypes';
import { ChargerAttributes, isChargerOffline } from '../models/charger-model';
import { getLastUsedCharger } from '../services/charger-service';
import { isSuccessful } from '../services/api-requests';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { runOnJS } from 'react-native-reanimated';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../store';
import { chargerActions } from '../store/slices/charger-slice';
import ChargerImageLoader from '../components/ChargerImageLoader';

const { width: screenWidth } = Dimensions.get('window');
const CARD_WIDTH = 365;

type ChargerStationNavigationProp = NativeStackNavigationProp<
  RootStackParamList,
  'ChargerStation'
>;

interface ChargeAgainCardProps {
  onDismiss?: () => void;
  baseTop?: number;
}

const ChargeAgainCard: React.FC<ChargeAgainCardProps> = ({
  onDismiss,
  baseTop = 0,
}) => {
  const [lastUsedCharger, setLastUsedCharger] =
    useState<ChargerAttributes | null>(null);
  const [isVisible, setIsVisible] = useState(true);
  // Use ref to track interactive area during gesture to avoid re-renders mid-gesture
  // which can cause crashes when the gesture handler and React state get out of sync
  const isInInteractiveAreaRef = useRef(true);
  const [pointerEventsEnabled, setPointerEventsEnabled] = useState(true);
  const navigation = useNavigation<ChargerStationNavigationProp>();
  const translateY = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(1)).current;

  const dispatch: AppDispatch = useDispatch();

  // Animation for the glow effect
  const glowAnim = useRef(new Animated.Value(0)).current;

  // console.log('ChargeAgainCard: Component rendered, isVisible:', isVisible);

  useEffect(() => {
    // console.log('ChargeAgainCard: useEffect triggered');
    const pulse = Animated.sequence([
      Animated.timing(glowAnim, {
        toValue: 1,
        duration: 1500,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: false,
      }),
      Animated.timing(glowAnim, {
        toValue: 0,
        duration: 1500,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: false,
      }),
    ]);

    // Loop the pulse forever
    Animated.loop(pulse).start();

    // Fetch last used charger
    const fetchLastUsedCharger = async () => {
      try {
        const response = await getLastUsedCharger();
        if (response && isSuccessful(response.status) && response.payload) {
          setLastUsedCharger(response.payload);
        } else {
          // No last used charger found
          //console.log('No last used charger found');
          onDismiss?.();
        }
      } catch (error) {
        console.error('Error fetching last used charger:', error);
        onDismiss?.();
      }
    };

    fetchLastUsedCharger();
  }, []);

  // Platform-specific shadow animations
  const platformStyles = Platform.select({
    ios: {
      shadowOpacity: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0.3, 0.8],
      }),
      shadowRadius: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [10, 30],
      }),
    },
    android: {
      elevation: glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [5, 20],
      }),
    },
  });

  // Web-specific boxShadow
  const webStyle =
    Platform.OS === 'web'
      ? {
          boxShadow: glowAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [
              '0px 0px 10px rgba(57, 190, 183, 0.3)',
              '0px 0px 30px rgba(57, 190, 183, 0.9)',
            ],
          }),
        }
      : {};

  const handleUpdate = (translationY: number) => {
    // Allow the card to move with the finger in any direction for better feedback
    translateY.setValue(translationY);

    // Only fade out for upward swipes
    if (translationY < 0) {
      opacity.setValue(1 + translationY / 200); // Fade out as it moves up
    } else {
      // Keep full opacity for downward or horizontal swipes
      opacity.setValue(1);
    }

    // Check if card is in interactive area (not blocking UI elements)
    // Use ref to avoid re-renders during gesture which can cause crashes
    const absoluteTop = baseTop + translationY;
    isInInteractiveAreaRef.current = absoluteTop > 50;

    // Live dismissal: if card is dragged above threshold, dismiss
    if (absoluteTop < 20) {
      setIsVisible(false);
      onDismiss?.();
    }
  };

  const handleEnd = (translationY: number, velocityY: number) => {
    // Improved threshold: If swiped up more than 150px OR with sufficient velocity (> -800), dismiss
    if (translationY < -150 || velocityY < -800) {
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: -300,
          duration: 300,
          // Must use JS driver (false) because we use .setValue() during gesture
          useNativeDriver: false,
        }),
        Animated.timing(opacity, {
          toValue: 0,
          duration: 300,
          useNativeDriver: false,
        }),
      ]).start(() => {
        setIsVisible(false);
        onDismiss?.();
      });
    } else {
      // Snap back to original position
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: 0,
          duration: 200,
          useNativeDriver: false,
        }),
        Animated.timing(opacity, {
          toValue: 1,
          duration: 200,
          useNativeDriver: false,
        }),
      ]).start(() => {
        // Reset interactive area when card snaps back
        // Safe to update state here since gesture has ended
        isInInteractiveAreaRef.current = true;
        setPointerEventsEnabled(true);
      });
    }
  };

  const panGesture = Gesture.Pan()
    .onUpdate((event) => {
      runOnJS(handleUpdate)(event.translationY);
    })
    .onEnd((event) => {
      runOnJS(handleEnd)(event.translationY, event.velocityY);
    });

  const handlePress = () => {
    if (lastUsedCharger) {
      // Hide the card immediately when pressed
      setIsVisible(false);
      onDismiss?.();

      dispatch(
        chargerActions.updateSelectedCharger({
          selectedCharger: lastUsedCharger,
        })
      );

      navigation.navigate('ChargerStation', {
        searchCharger: true,
      });
    }
  };

  if (!isVisible || !lastUsedCharger) {
    // For testing: show a placeholder card even without charger data
    if (isVisible) {
      return (
        <GestureDetector gesture={panGesture}>
          <Animated.View
            style={[
              styles.cardContainer,
              platformStyles,
              webStyle,
              {
                transform: [{ translateY }],
                opacity,
              },
            ]}
        pointerEvents={pointerEventsEnabled ? 'auto' : 'none'}
      >
        {/* Close (X) button */}
        <Pressable
          onPress={() => {
            setIsVisible(false);
            onDismiss?.();
          }}
          style={styles.closeButtonContainer}
          hitSlop={10}
        >
          <MaterialIcons name="close" size={24} color="#8D8E90" />
        </Pressable>
        <Pressable onPress={handlePress} style={styles.pressableContainer}>
          <View style={styles.headerContainer}>
            <Text style={styles.headerText}>Charge Again!</Text>
            <Text style={styles.subText}>
              Tap here to charge again at your last-used charger
            </Text>
          </View>

          <View style={styles.chargerInfoContainer}>
            <ChargerImageLoader
              chargerId={lastUsedCharger?.uuid}
              style={styles.chargerImage}
              fallbackSource={require('../../assets/chargerStation.png')}
              resizeMode="cover"
              alt="Charger station"
              circular={true}
            />

            <View style={styles.chargerDetailsContainer}>
              <Text style={styles.chargerName}>
                Loading charger info...
              </Text>
              <View style={styles.statusContainer}>
                <View
                  style={[
                    styles.statusIndicator,
                    {
                      backgroundColor: '#8D8E90', // Gray for loading state
                    },
                  ]}
                />
                <Text style={styles.statusText}>Loading status...</Text>
              </View>
              <Text style={styles.chargerLocation}>
                Fetching last used charger
              </Text>
              <Text style={styles.chargerRates}>
                Rates will appear here
              </Text>
            </View>

            <View style={styles.iconContainer}>
              <MaterialIcons
                name="keyboard-arrow-right"
                size={24}
                color="white"
              />
            </View>
          </View>
        </Pressable>
      </Animated.View>
    </GestureDetector>
  );
}
return null;
  }

  return (
    <GestureDetector gesture={panGesture}>
      <Animated.View
        style={[
          styles.cardContainer,
          platformStyles,
          webStyle,
          {
            transform: [{ translateY }],
            opacity,
          },
        ]}
        pointerEvents={pointerEventsEnabled ? 'auto' : 'none'}
      >
        {/* Close (X) button */}
        <Pressable
          onPress={() => {
            setIsVisible(false);
            onDismiss?.();
          }}
          style={styles.closeButtonContainer}
          hitSlop={10}
        >
          <MaterialIcons name="close" size={24} color="#8D8E90" />
        </Pressable>
        <Pressable onPress={handlePress} style={styles.pressableContainer}>
          <View style={styles.headerContainer}>
            <Text style={styles.headerText}>Charge Again!</Text>
            <Text style={styles.subText}>
              Tap here to charge again at your last-used charger
            </Text>
          </View>

          <View style={styles.chargerInfoContainer}>
            <ChargerImageLoader
              chargerId={lastUsedCharger?.uuid}
              style={styles.chargerImage}
              fallbackSource={require('../../assets/chargerStation.png')}
              resizeMode="cover"
              alt="Charger station"
              circular={true}
            />

            <View style={styles.chargerDetailsContainer}>
              <Text style={styles.chargerName}>{lastUsedCharger.name}</Text>
              <View style={styles.statusContainer}>
                <View
                  style={[
                    styles.statusIndicator,
                    {
                      backgroundColor: isChargerOffline(lastUsedCharger)
                        ? '#6B7280'
                        : lastUsedCharger.isOccupied
                        ? '#F97777'
                        : '#39BEB7',
                    },
                  ]}
                />
                <Text style={styles.statusText}>
                  {isChargerOffline(lastUsedCharger) ? 'Charger Offline' : lastUsedCharger.isOccupied ? 'In Use' : 'Available'}
                </Text>
              </View>
              <Text style={styles.chargerLocation}>
                {lastUsedCharger.city}, {lastUsedCharger.state}
              </Text>
              <Text style={styles.chargerRates}>
                Peak: $
                {lastUsedCharger.financials?.price_kwh_peak ||
                  lastUsedCharger.price_hourly_peak}
                /kWh • Off-Peak: $
                {lastUsedCharger.financials?.price_kwh_off_peak ||
                  lastUsedCharger.price_hourly_off_peak}
                /kWh
              </Text>
            </View>

            <View style={styles.iconContainer}>
              <MaterialIcons
                name="keyboard-arrow-right"
                size={24}
                color="white"
              />
            </View>
          </View>
        </Pressable>
      </Animated.View>
    </GestureDetector>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    width: CARD_WIDTH,
    minHeight: 120,
    backgroundColor: '#0B1E1D',
    borderColor: '#39BEB7',
    borderWidth: 2,
    borderRadius: 16,
    shadowColor: Platform.OS === 'ios' ? '#39BEB7' : undefined,
    shadowOffset: Platform.OS === 'ios' ? { width: 0, height: 0 } : undefined,
    elevation: Platform.OS === 'android' ? 5 : undefined,
    zIndex: 1000,
  },
  closeButtonContainer: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1100,
    backgroundColor: 'transparent',
    padding: 4,
    borderRadius: 12,
  },
  pressableContainer: {
    flex: 1,
    padding: 16,
  },
  headerContainer: {
    marginBottom: 12,
  },
  headerText: {
    fontFamily: 'PlusJakartaSans-Bold',
    fontSize: 18,
    color: '#FFFFFF',
    marginBottom: 4,
  },
  subText: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 12,
    color: '#8D8E90',
  },
  chargerInfoContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  chargerImage: {
    width: 50,
    height: 50,
    borderRadius: 25,
    marginRight: 12,
    marginTop: 2,
  },
  chargerDetailsContainer: {
    flex: 1,
    minWidth: 0,
  },
  chargerName: {
    fontFamily: 'PlusJakartaSans-Bold',
    fontSize: 14,
    color: '#FFFFFF',
    marginBottom: 2,
    flexWrap: 'wrap',
  },
  chargerLocation: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 12,
    color: '#8D8E90',
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  chargerRates: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 10,
    color: '#8D8E90',
    flexWrap: 'wrap',
    lineHeight: 14,
  },
  iconContainer: {
    width: 24,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 2,
  },
  statusContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 4,
  },
  statusText: {
    fontFamily: 'PlusJakartaSans-Regular',
    fontSize: 10,
    color: '#8D8E90',
  },
});

export default ChargeAgainCard;
